import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleGuard } from './RoleGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';

// Dashboard
import { DashboardPage } from '@/pages/dashboard/DashboardPage';

// CRM Pages
import { LeadListPage } from '@/pages/crm/leads/LeadListPage';
import { LeadDetailPage } from '@/pages/crm/leads/LeadDetailPage';
import { LeadCreatePage } from '@/pages/crm/leads/LeadCreatePage';
import { ContactListPage } from '@/pages/crm/contacts/ContactListPage';
import { ContactDetailPage } from '@/pages/crm/contacts/ContactDetailPage';
import { ContactCreatePage } from '@/pages/crm/contacts/ContactCreatePage';
import { CompanyListPage } from '@/pages/crm/companies/CompanyListPage';
import { CompanyDetailPage } from '@/pages/crm/companies/CompanyDetailPage';
import { CompanyCreatePage } from '@/pages/crm/companies/CompanyCreatePage';
import { DealPipelinePage } from '@/pages/crm/deals/DealPipelinePage';
import { DealListPage } from '@/pages/crm/deals/DealListPage';
import { DealDetailPage } from '@/pages/crm/deals/DealDetailPage';
import { DealCreatePage } from '@/pages/crm/deals/DealCreatePage';
import { TaskListPage } from '@/pages/crm/tasks/TaskListPage';
import { TaskCreatePage } from '@/pages/crm/tasks/TaskCreatePage';
import { TaskDetailPage } from '@/pages/crm/tasks/TaskDetailPage';
import { NotesPage } from '@/pages/crm/notes/NotesPage';

// Expense Pages
import { ExpenseListPage } from '@/pages/expenses/ExpenseListPage';
import { ExpenseDetailPage } from '@/pages/expenses/ExpenseDetailPage';
import { ExpenseCreatePage } from '@/pages/expenses/ExpenseCreatePage';
import { ExpenseCategoriesPage } from '@/pages/expenses/ExpenseCategoriesPage';
import { ExpenseApprovalsPage } from '@/pages/expenses/ExpenseApprovalsPage';

// Financial Pages
import { FinancialOverviewPage } from '@/pages/financial/FinancialOverviewPage';
import { InvoiceListPage } from '@/pages/financial/InvoiceListPage';
import { InvoiceDetailPage } from '@/pages/financial/InvoiceDetailPage';
import { InvoiceCreatePage } from '@/pages/financial/InvoiceCreatePage';
import { QuotationListPage } from '@/pages/financial/QuotationListPage';
import { QuotationCreatePage } from '@/pages/financial/QuotationCreatePage';
import { QuotationDetailPage } from '@/pages/financial/QuotationDetailPage';
import { AgreementListPage } from '@/pages/financial/AgreementListPage';
import { AgreementCreatePage } from '@/pages/financial/AgreementCreatePage';
import { AgreementDetailPage } from '@/pages/financial/AgreementDetailPage';
import { PaymentListPage } from '@/pages/financial/PaymentListPage';
import { AccountListPage } from '@/pages/financial/AccountListPage';
import { TransactionListPage } from '@/pages/financial/TransactionListPage';
import { BudgetListPage } from '@/pages/financial/BudgetListPage';
import { ReportsPage } from '@/pages/financial/ReportsPage';

// Payroll Pages
import { EmployeeListPage } from '@/pages/payroll/EmployeeListPage';
import { EmployeeCreatePage } from '@/pages/payroll/EmployeeCreatePage';
import { EmployeeDetailPage } from '@/pages/payroll/EmployeeDetailPage';
import { PayComponentsPage } from '@/pages/payroll/PayComponentsPage';
import { PayslipListPage } from '@/pages/payroll/PayslipListPage';
import { PayslipGeneratePage } from '@/pages/payroll/PayslipGeneratePage';
import { PayslipDetailPage } from '@/pages/payroll/PayslipDetailPage';
import { PayrollRunListPage } from '@/pages/payroll/PayrollRunListPage';
import { PayrollRunDetailPage } from '@/pages/payroll/PayrollRunDetailPage';
import { MyPayslipsPage } from '@/pages/payroll/MyPayslipsPage';

// Settings Pages
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { ProfilePage } from '@/pages/settings/ProfilePage';
import { TeamPage } from '@/pages/settings/TeamPage';
import { PipelineSettingsPage } from '@/pages/settings/PipelineSettingsPage';
import { DocumentSettingsPage } from '@/pages/settings/documents/DocumentSettingsPage';

// Other
import { NotFoundPage } from '@/pages/NotFoundPage';

export const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <ErrorBoundary>
          <AppLayout />
        </ErrorBoundary>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },

      // CRM
      { path: 'leads', element: <LeadListPage /> },
      { path: 'leads/new', element: <LeadCreatePage /> },
      { path: 'leads/:id/edit', element: <LeadCreatePage /> },
      { path: 'leads/:id', element: <LeadDetailPage /> },

      { path: 'contacts', element: <ContactListPage /> },
      { path: 'contacts/new', element: <ContactCreatePage /> },
      { path: 'contacts/:id/edit', element: <ContactCreatePage /> },
      { path: 'contacts/:id', element: <ContactDetailPage /> },

      { path: 'companies', element: <CompanyListPage /> },
      { path: 'companies/new', element: <CompanyCreatePage /> },
      { path: 'companies/:id/edit', element: <CompanyCreatePage /> },
      { path: 'companies/:id', element: <CompanyDetailPage /> },

      { path: 'deals', element: <DealListPage /> },
      { path: 'deals/pipeline', element: <DealPipelinePage /> },
      { path: 'deals/pipeline/:pipelineId', element: <DealPipelinePage /> },
      { path: 'deals/new', element: <DealCreatePage /> },
      { path: 'deals/:id/edit', element: <DealCreatePage /> },
      { path: 'deals/:id', element: <DealDetailPage /> },

      { path: 'tasks', element: <TaskListPage /> },
      { path: 'tasks/new', element: <TaskCreatePage /> },
      { path: 'tasks/:id/edit', element: <TaskCreatePage /> },
      { path: 'tasks/:id', element: <TaskDetailPage /> },

      { path: 'notes', element: <NotesPage /> },

      // Expenses
      { path: 'expenses', element: <ExpenseListPage /> },
      { path: 'expenses/new', element: <ExpenseCreatePage /> },
      { path: 'expenses/categories', element: <ExpenseCategoriesPage /> },
      { path: 'expenses/approvals', element: <ExpenseApprovalsPage /> },
      { path: 'expenses/:id', element: <ExpenseDetailPage /> },

      // Financial
      { path: 'financial', element: <FinancialOverviewPage /> },
      { path: 'financial/invoices', element: <InvoiceListPage /> },
      { path: 'financial/invoices/new', element: <InvoiceCreatePage /> },
      { path: 'financial/invoices/:id', element: <InvoiceDetailPage /> },
      { path: 'financial/invoices/:id/edit', element: <InvoiceCreatePage /> },
      { path: 'financial/quotations', element: <QuotationListPage /> },
      { path: 'financial/quotations/new', element: <QuotationCreatePage /> },
      { path: 'financial/quotations/:id', element: <QuotationDetailPage /> },
      { path: 'financial/quotations/:id/edit', element: <QuotationCreatePage /> },
      { path: 'financial/agreements', element: <AgreementListPage /> },
      { path: 'financial/agreements/new', element: <AgreementCreatePage /> },
      { path: 'financial/agreements/:id', element: <AgreementDetailPage /> },
      { path: 'financial/agreements/:id/edit', element: <AgreementCreatePage /> },
      { path: 'financial/payments', element: <PaymentListPage /> },
      { path: 'financial/accounts', element: <AccountListPage /> },
      { path: 'financial/transactions', element: <TransactionListPage /> },
      { path: 'financial/budgets', element: <BudgetListPage /> },
      { path: 'financial/reports', element: <ReportsPage /> },

      // People / Payroll — salary data, so MANAGER+ throughout
      { path: 'people/employees',          element: <RoleGuard role="MANAGER"><EmployeeListPage /></RoleGuard> },
      { path: 'people/employees/new',      element: <RoleGuard role="ADMIN"><EmployeeCreatePage /></RoleGuard> },
      { path: 'people/employees/:id/edit', element: <RoleGuard role="ADMIN"><EmployeeCreatePage /></RoleGuard> },
      { path: 'people/employees/:id',      element: <RoleGuard role="MANAGER"><EmployeeDetailPage /></RoleGuard> },
      { path: 'people/pay-components',     element: <RoleGuard role="MANAGER"><PayComponentsPage /></RoleGuard> },
      { path: 'people/payslips',           element: <RoleGuard role="MANAGER"><PayslipListPage /></RoleGuard> },
      { path: 'people/payslips/new',       element: <RoleGuard role="ADMIN"><PayslipGeneratePage /></RoleGuard> },
      { path: 'people/payslips/:id',       element: <RoleGuard role="MANAGER"><PayslipDetailPage /></RoleGuard> },
      { path: 'people/payroll-runs',       element: <RoleGuard role="MANAGER"><PayrollRunListPage /></RoleGuard> },
      { path: 'people/payroll-runs/:id',   element: <RoleGuard role="MANAGER"><PayrollRunDetailPage /></RoleGuard> },
      // Self-service: no RoleGuard — every signed-in user may see their OWN
      // payslips, and the server scopes the data to them.
      { path: 'my-payslips',               element: <MyPayslipsPage /> },

      // Settings
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/profile', element: <ProfilePage /> },
      { path: 'settings/team', element: <RoleGuard role="ADMIN"><TeamPage /></RoleGuard> },
      { path: 'settings/pipelines', element: <RoleGuard role="ADMIN"><PipelineSettingsPage /></RoleGuard> },
      { path: 'settings/documents',    element: <RoleGuard role="ADMIN"><DocumentSettingsPage /></RoleGuard> },
      // Back-compat redirects for the old split settings pages
      { path: 'settings/organization', element: <Navigate to="/settings/documents?tab=general" replace /> },
      { path: 'settings/agreements',   element: <Navigate to="/settings/documents?tab=agreement" replace /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
